/**
 * Which chat sessions have had their end-of-session rating answered (rated or
 * dismissed), so the row under the last reply asks at most once per session.
 *
 * Kept in `localStorage` because "once" has to survive a reload: reopening a
 * rated conversation from history must not ask again. Capped to the newest
 * `MAX_SESSIONS` ids, and every read and write is guarded: storage that is
 * blocked or full degrades to "once per page load", never to an error.
 *
 * `shown` is page-load memory only. It keeps `Session Rating Shown` to one
 * event per session, however often the row re-renders.
 */

const STORAGE_KEY = 'bower.sessionRating.answered';
const MAX_SESSIONS = 200;

const answered = new Set<string>(readStored());
const shown = new Set<string>();

function readStored(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function isSessionRatingAnswered(sessionId: string): boolean {
  return answered.has(sessionId);
}

export function markSessionRatingAnswered(sessionId: string): void {
  answered.delete(sessionId);
  answered.add(sessionId);
  while (answered.size > MAX_SESSIONS) {
    const oldest = answered.values().next().value;
    if (oldest === undefined) break;
    answered.delete(oldest);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...answered]));
  } catch {
    // Blocked or full: the in-memory set still holds it for this page load.
  }
}

/** True the first time it is called for a session in this page load. */
export function takeFirstShow(sessionId: string): boolean {
  if (shown.has(sessionId)) return false;
  shown.add(sessionId);
  return true;
}
